import React from 'react';
import {
  Button,
  Chip,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove';
import moment from 'moment';

import {
  AccountTable,
  DashboardError,
  DashboardPage,
  IAccountTableColumn,
  JobPublicationDetails,
} from '../../components/JobsAccount';
import { CPGJOBS_SITE_URL } from '../../components/CpgChrome/cpgSites';
import {
  useGetMySavedJobsQuery,
  useRemoveSavedJobMutation,
} from '../../services/serviceJobsAccount';
import { ISavedJob } from '../../services/serviceJobsAccount/interfaces';

const formatDate = (date: string | null): string => (
  date ? moment(date).format('MM/DD/YYYY') : '—'
);

export const SavedJobs = () => {
  const saved = useGetMySavedJobsQuery();
  const [removeSavedJob, removeResult] = useRemoveSavedJobMutation();
  const rows = saved.data || [];

  const columns: IAccountTableColumn<ISavedJob>[] = [
    {
      id: 'job',
      label: 'Job',
      render: (job) => (
        <JobPublicationDetails
          title={job.jobTitle || 'Job listing'}
          path={job.slug}
          siteId={job.siteId}
          isActive={job.isActive}
          titleSx={{ fontSize: '1rem', fontWeight: 700 }}
        />
      ),
    },
    {
      id: 'company',
      label: 'Company',
      render: (job) => job.company || '—',
    },
    {
      id: 'savedDate',
      label: 'Saved',
      width: 140,
      render: (job) => formatDate(job.savedDate),
    },
    {
      id: 'status',
      label: 'Status',
      width: 210,
      render: (job) => (
        <Chip
          size="small"
          color={job.isActive ? 'success' : 'default'}
          label={job.isActive ? 'Active' : 'No longer accepting applications'}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      width: 190,
      render: (job) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          {job.isActive && job.slug ? (
            <Button
              component="a"
              href={`${CPGJOBS_SITE_URL}/jobs/${job.slug}/apply`}
              size="small"
              variant="outlined"
            >
              Apply
            </Button>
          ) : null}
          <Tooltip title="Remove saved job">
            <span>
              <IconButton
                aria-label={`Remove ${job.jobTitle || 'job'} from saved jobs`}
                disabled={!job.slug || removeResult.isLoading}
                onClick={() => job.slug && removeSavedJob(job.slug)}
              >
                <BookmarkRemoveIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <DashboardPage
      title="Saved Jobs"
      actions={(
        <Button component="a" href={`${CPGJOBS_SITE_URL}/jobs`} variant="contained">
          Browse Jobs
        </Button>
      )}
    >
      <DashboardError
        error={saved.error || removeResult.error}
        fallback="We could not update your saved jobs. Please try again."
      />
      <AccountTable
        ariaLabel="Saved jobs"
        columns={columns}
        rows={rows}
        getRowId={(job) => job.id}
        emptyMessage="No saved jobs yet. Browse jobs to build your shortlist."
        isLoading={saved.isLoading || saved.isFetching}
      />
    </DashboardPage>
  );
};

export default SavedJobs;
